import { useRef, type ReactElement } from 'react';

/*
 * ⚠ No `Notice` — and its absence is the decision, not an omission. `D3`'s
 * one-channel rule (§5.7) plus `lib/boards.ts` announcing its own rollback
 * left the error path a plain node rather than a `role="alert"`, which
 * orphaned the import this file was stubbed with. Removed by agent H at
 * integration because `pnpm lint` rejects it for the whole chunk; see the two
 * comments below that explain what is deliberately NOT rendered here.
 */
import { Button, Dialog } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * `FE-08.3` — the two destructive confirmations, chunk `D3`, agent E.
 *
 * One component and one state variable for both, because they differ only in
 * their copy and their verb: `action` is `'close' | 'delete' | null`, and
 * `null` is the closed state. Two components would be two dismissal ladders,
 * two focus contracts and two places to get the board's title wrong.
 *
 * Built on the shipped `Dialog` at `size="sm"` — the confirm size — with the
 * destructive action as a `Button variant="danger"` in the `footer` slot and
 * Cancel beside it. `Dialog` is the right primitive **here** and the wrong one
 * for the board menu itself (`D3-BRIEF.md` §2.1): a confirmation wants exactly
 * the focus trap and scrim that a non-modal side panel must not have.
 *
 * **The board's title appears in the body of both.** `BE-08.2`'s own line is
 * "the confirmation naming what will be destroyed", and the delete copy says
 * the word *permanently* and that it cannot be undone.
 *
 * ⚠ **No typed-name confirmation.** `DELETE /boards/:id` takes **no body**,
 * unlike workspace deletion's `{ confirmName }`. Inventing one client-side
 * would be a second, weaker spelling of a protection the server does not have,
 * and would read as a guarantee it cannot make.
 *
 * ⚠ Delete is reachable **only on an already-closed board** — `FR-EXP-012` AC4,
 * and the server agrees: `DELETE /boards/:id` answers 409 unless the board is
 * closed. That gate is the panel's (§2.3), not this component's; this one
 * renders whatever it is asked to.
 *
 * ⚠ **No `announce()` here, and no live region either.** `error` renders
 * inline, next to the action, as a **plain node with no role** — `lib/boards.ts`
 * announces every mutation's rollback itself, so a `Notice tone="bad"`
 * (`role="alert"`) here would put the same failure into the accessibility tree
 * twice (§5.7). The visible node stays because the alternative — rendering
 * nothing — left a sighted mouse user with no feedback at all on a failed
 * close or delete. One *announcement* channel, two *renderings*. Success has
 * nowhere to be announced
 * from *this* file either: closed/reopened routes through `BoardClosedNotice`'s
 * own `role="status"` and deleted takes the member away from a board that can
 * no longer announce anything — both are the host's channel, not this one's.
 *
 * ⚠ Default focus lands on **Cancel**, not the danger button. Radix's default
 * autofocus is "first tabbable in the content", which here would be the
 * destructive action — an Enter keypress a heartbeat too early would confirm
 * it. `initialFocus` overrides that to the control that does nothing.
 *
 * ⚠ **Cancel is never natively `disabled`, even while `busy`.** `Button`'s own
 * file says why: a focused control that goes native-`disabled` mid-interaction
 * drops focus to `<body>` — and focus sits on Cancel by default (the point
 * above), inside a focus-trapped modal, which is exactly where that failure
 * bites hardest. The danger button's own `busy` click guard is what stops a
 * second submit; nothing here needs to also lock Cancel, and `disabled`
 * without a `disabledReason` is meaningless by `Button`'s own rule anyway —
 * this isn't a permission gate.
 */

export type BoardDangerAction = 'close' | 'delete';

export interface BoardDangerConfirmProps {
  /** `null` closes the dialog. One component, two confirmations, one state variable. */
  action: BoardDangerAction | null;
  boardTitle: string;
  busy?: boolean | undefined;
  error?: string | null | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * ⚠ THE SENTINEL-SPLIT IDIOM, copied verbatim from `BoardPowerUpList.tsx`,
 * whose own comment names this file's two body sites as the next two and says:
 * *copy this; do not re-decide it.*
 *
 * Both bodies emphasise the board's title inside a sentence, wrapped in a
 * `<strong>`. Two fragment keys (a prefix and a suffix) would fix word order
 * and the separator for every locale; one key without the `<strong>` would
 * remove emphasis from a **destructive** confirmation. Taken instead: one
 * whole-sentence key with a `{board}` slot, resolved to a codepoint no
 * translation can contain and split on it at render, so a locale keeps full
 * freedom to move the slot anywhere in the sentence.
 *
 * ⚠ THE ONE-PART FALLBACK ARM IS NOT OPTIONAL. A translation that omits
 * `{board}` yields one part, and rendering `parts[0]` alone would silently
 * DELETE THE BOARD'S NAME from a destructive confirmation. The plain arm
 * degrades it to a correct, unemphasised sentence instead.
 */
const NAME_SENTINEL = String.fromCharCode(0);

/**
 * ⚠ KEYS, NOT STRINGS. This is a module constant, and a module constant cannot
 * call `t()` — the catalogue is resolved from React context. So it holds the
 * key and the render resolves it (the `workspace-switcher` idiom).
 */
interface DangerCopy {
  dialogTitleKey: string;
  bodyKey: string;
  confirmLabelKey: string;
  busyLabelKey: string;
}

const COPY: Record<BoardDangerAction, DangerCopy> = {
  close: {
    dialogTitleKey: 'boardAdmin.danger.close.title',
    bodyKey: 'boardAdmin.danger.close.body',
    confirmLabelKey: 'boardAdmin.danger.close.confirm',
    busyLabelKey: 'boardAdmin.danger.close.busy',
  },
  delete: {
    dialogTitleKey: 'boardAdmin.danger.delete.title',
    bodyKey: 'boardAdmin.danger.delete.body',
    confirmLabelKey: 'boardAdmin.danger.delete.confirm',
    busyLabelKey: 'boardAdmin.danger.delete.busy',
  },
};

export function BoardDangerConfirm({
  action,
  boardTitle,
  busy,
  error,
  onConfirm,
  onCancel,
}: BoardDangerConfirmProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const isBusy = busy === true;
  // `action` is only ever read while non-null (`open` gates rendering inside
  // `Dialog`'s portal), so the fallback here is never seen — it exists purely
  // to keep the lookup total while the dialog is closed.
  const copy = COPY[action ?? 'close'];
  const bodyParts = t(copy.bodyKey, { board: NAME_SENTINEL }).split(NAME_SENTINEL);

  return (
    <Dialog
      open={action !== null}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      title={t(copy.dialogTitleKey)}
      size="sm"
      initialFocus={cancelRef}
      footer={
        <>
          <Button
            variant="danger"
            busy={isBusy}
            busyLabel={t(copy.busyLabelKey)}
            onClick={onConfirm}
          >
            {t(copy.confirmLabelKey)}
          </Button>
          <Button ref={cancelRef} variant="quiet" onClick={onCancel}>
            {t('boardAdmin.danger.cancel')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-ink-muted">
        {/* ⚠ The one-part arm is the fallback for a translation that drops
            `{board}`; it keeps the board's name in the sentence rather than
            deleting it. See `NAME_SENTINEL` above. */}
        {bodyParts.length === 2 ? (
          <p>
            {bodyParts[0]}
            <strong className="font-medium text-ink">{boardTitle}</strong>
            {bodyParts[1]}
          </p>
        ) : (
          <p>{t(copy.bodyKey, { board: boardTitle })}</p>
        )}
        {/*
         * ⚠ **A plain node, deliberately — not `Notice tone="bad"`.**
         *
         * Agent B reported the gap this closes: `lib/boards.ts` announces every
         * mutation's rollback itself, so a `Notice` here would be `role="alert"`
         * and the same failure would reach the accessibility tree **twice**,
         * which §5.7's one-channel rule forbids. Passing nothing at all was the
         * other way out, and it left a **sighted mouse user with no feedback on
         * a failed close or delete at all** — the dialog simply stayed open.
         *
         * The rule is one *announcement* channel, not one *rendering*. A node
         * with no live role is the visible half of a failure whose audible half
         * `announceRollback` already owns, so both members are served and
         * neither is served twice. The confirmation stays open so the action is
         * retryable.
         */}
        {error != null ? (
          <p className="text-card text-bad" data-testid="board-danger-error">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
